import React, { useState, useEffect } from "react";
import {
  Input,
  Form,
  Col,
  Row,
  Button,
  Select,
  DatePicker,
  Tooltip,
  notification,
} from "antd";
import { SaveOutlined, InfoCircleOutlined } from "@ant-design/icons";
import CurrencyInput from "react-currency-input";
import RequestHandler from "util/RequestHandler";
import { defaultValidateMessages } from "./Helper";
import moment from "moment";

const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 8 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 16 },
  },
};

const chargeTypeInfo = (
  <div style={{ maxWidth: 320 }}>
    <p style={{ marginBottom: 6 }}>
      <b>BEN:</b> Tüm masraf ve kesintiler alıcıya aittir. Gönderilen tutardan
      masraflar düşülerek alıcının hesabına aktarılır.
    </p>
    <p style={{ marginBottom: 6 }}>
      <b>SHA:</b> Gönderici kendi bankasının kesintilerini öder; alıcı ise aracı
      banka ve kendi bankasının masraflarını karşılar. Bu durumda alıcının
      hesabına gönderilen paradan daha azı geçer.
    </p>
    <p style={{ marginBottom: 0 }}>
      <b>OUR:</b> Tüm transfer ve aracı banka masrafları göndericiye aittir.
      Alıcı, gönderilen tutarın tamamını (net olarak) eksiksiz alır.
    </p>
  </div>
);

const formatAmountForInput = (amount) => {
  if (amount === null || amount === undefined || amount === "") return "";
  const num = typeof amount === "number" ? amount : parseFloat(String(amount).replace(",", "."));
  if (Number.isNaN(num)) return String(amount);
  return num.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const SwiftPaymentUpdateForm = (props) => {
  const [form] = Form.useForm();
  const { Option } = Select;

  const [loading, setLoading] = useState(false);
  const [currencySelect, setCurrencySelect] = useState({ data: [], loading: true });
  const [currencyId, setCurrencyId] = useState(null);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [chargeTypes, setChargeTypes] = useState([]);
  const [transactionDetails, setTransactionDetails] = useState([]);
  const [swiftBankId, setSwiftBankId] = useState(null);
  const [title, setTitle] = useState("");
  const [iban, setIban] = useState("");

  useEffect(() => {
    RequestHandler.get("/Payment/CurrencyList").then((response) => {
      const data = response.data || [];
      setCurrencySelect({ data, loading: false });
    });

    RequestHandler.get("/Payment/PaymentTypes?isSwiftTransfer=true")
      .then((data) => setPaymentTypes(Array.isArray(data) ? data : []))
      .catch(() => setPaymentTypes([]));

    RequestHandler.get("/Payment/SwiftChargeTypes")
      .then((data) => setChargeTypes(Array.isArray(data) ? data : []))
      .catch(() => setChargeTypes([]));

    RequestHandler.get("/Payment/SwiftTransactionDetails")
      .then((data) => setTransactionDetails(Array.isArray(data) ? data : []))
      .catch(() => setTransactionDetails([]));

    RequestHandler.get(`/bank?$filter=isHasSwiftTransfer eq true`).then((bankRes) => {
      const swiftBanks = bankRes.value || [];
      setSwiftBankId(swiftBanks.length > 0 ? swiftBanks[0].id : null);
    });
  }, []);

  useEffect(() => {
    if (props.data) {
      const data = { ...props.data };
      data.paymentDate = data.paymentDate ? moment(data.paymentDate) : null;
      data.amount = formatAmountForInput(data.amount);
      setTitle(data.title || "");
      setIban(data.iban || "");
      setCurrencyId(data.currencyId);
      form.setFieldsValue(data);
    }
  }, [props.data]);

  const onChangeTitle = (e) => setTitle(e.target.value);
  const onChangeIban = (e) => setIban(e.target.value.toUpperCase());
  const onChangeCurrencyId = (value) => setCurrencyId(value);

  const onFinish = (values) => {
    const bankId = values.bankId || swiftBankId || props.data?.bankId;
    if (!bankId) {
      setLoading(false);
      notification["error"]({
        message: "Swift destekli banka tanımlı değil.",
        duration: 5,
      });
      return;
    }

    values.id = props.data?.id;
    values.paymentDate = values.paymentDate.format("YYYY-MM-DD HH:mm:ss");
    values.AmountString = values.amount;
    values.amount = 1;
    values.isFromWeb = true;
    values.isSwiftPayment = true;
    values.bankId = bankId;
    values.checkSamePayment = false;

    if (values.description) {
      values.description = values.description.trimEnd();
    }

    RequestHandler.put("/Payment", values, true).then((response) => {
      setLoading(false);
      if (response && response.success) {
        if (props.onSuccess) props.onSuccess();
      }
    });
  };

  const onFinishFailed = () => setLoading(false);

  return (
    <Form
      {...formItemLayout}
      validateMessages={defaultValidateMessages}
      form={form}
      name="swiftPaymentUpdate"
      fields={[
        { name: "title", value: title },
        { name: "iban", value: iban },
        { name: "currencyId", value: currencyId },
      ]}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
    >
      <Row>
        <Col span={24}>
          <Form.Item name="id" hidden>
            <Input />
          </Form.Item>

          <Form.Item label="Ad Soyad / Ünvan" name="title" rules={[{ required: true }]}>
            <Input value={title} onChange={onChangeTitle} />
          </Form.Item>

          <Form.Item
            label="Swift Kod"
            name="swiftCode"
            extra="8 haneli girilirse XXX ile 11 haneye tamamlanır."
            rules={[
              { required: true, message: "Lütfen swift kodunu giriniz." },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  const cleaned = value.replace(/\s+/g, "");
                  if (cleaned.length !== 8 && cleaned.length !== 11)
                    return Promise.reject("Swift kodu 8 veya 11 karakter olmalıdır.");
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              maxLength={11}
              onChange={(e) => {
                form.setFieldsValue({
                  swiftCode: e.target.value.replace(/\s+/g, "").toUpperCase(),
                });
              }}
            />
          </Form.Item>

          <Form.Item
            label="Swift Banka Unvan"
            name="swiftBankName"
            rules={[{ required: true, message: "Lütfen swift banka unvanını giriniz." }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="Iban Kodu"
            name="iban"
            onChange={onChangeIban}
            rules={[
              { required: true, message: "Lütfen IBAN alanını boş bırakmayın." },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  const cleaned = value.replace(/\s+/g, "").toUpperCase();
                  if (cleaned.length < 15 || cleaned.length > 34)
                    return Promise.reject("IBAN 15-34 karakter olmalıdır.");
                  if (!/^[A-Z]{2}[0-9A-Z]+$/.test(cleaned))
                    return Promise.reject("IBAN formatı geçersiz.");
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input type="text" maxLength={34} onFocus={(event) => event.target.select()} />
          </Form.Item>

          <Form.Item label="Tutar" name="amount" rules={[{ required: true }]}>
            <CurrencyInput
              style={{ width: "100%" }}
              className="ant-input"
              decimalSeparator=","
              thousandSeparator="."
            />
          </Form.Item>

          <Form.Item label="Para Birimi" name="currencyId" rules={[{ required: true }]}>
            <Select
              allowClear
              showSearch
              onChange={onChangeCurrencyId}
              filterOption={(input, option) =>
                option.children
                  .toLocaleLowerCase("tr-TR")
                  .indexOf(input.toLocaleLowerCase("tr-TR")) >= 0
              }
            >
              {currencySelect.data.map(({ id, code }) => (
                <Option key={id} value={id}>
                  {code}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Fatura Numarası"
            name="invoiceNumber"
            rules={[{ required: true, message: "Lütfen fatura numarası giriniz." }]}
          >
            <Input placeholder="Giriniz" />
          </Form.Item>

          <Form.Item
            label={
              <span>
                Masraf&nbsp;
                <Tooltip title={chargeTypeInfo}>
                  <InfoCircleOutlined />
                </Tooltip>
              </span>
            }
            name="swiftChargeType"
            rules={[{ required: true, message: "Lütfen masraf tipini seçiniz." }]}
          >
            <Select placeholder="Seçiniz">
              {chargeTypes.map((ct) => (
                <Option key={ct.value} value={ct.value}>
                  {ct.text}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Ödeme/Talimat/Valör Tarihi"
            name="paymentDate"
            rules={[{ required: true }]}
          >
            <DatePicker format="DD.MM.YYYY" placeholder="Tarih seç" />
          </Form.Item>

          <Form.Item label="İşlem tipi" name="paymentTypeId">
            <Select allowClear showSearch placeholder="Seçiniz" optionFilterProp="children">
              {paymentTypes.map((pt) => (
                <Option key={pt.id} value={pt.id}>
                  {pt.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="İşlem Detayı" name="swiftTransactionDetail">
            <Select allowClear showSearch placeholder="Seçiniz" optionFilterProp="children">
              {transactionDetails.map((td) => (
                <Option key={td.value} value={td.value}>
                  {td.value} - {td.text}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Açıklama" name="description">
            <Input.TextArea rows={3} />
          </Form.Item>

          <Col style={{ textAlign: "right" }}>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              htmlType="submit"
              onClick={() => setLoading(true)}
              loading={loading}
            >
              Güncelle
            </Button>
          </Col>
        </Col>
      </Row>
    </Form>
  );
};

export default SwiftPaymentUpdateForm;
